import * as d3 from "d3";
import { useAtom } from "jotai";
import { useEffect, useMemo, useRef } from "react";
import { useResizeObserver } from "../../../../../ui/Plot/hooks";
import { formatDate } from "../../../../../utils/dates";
import { selectionContextAtom } from "../../state";
import { computePlotTicks, TIMELINE_MARGINS } from "./plot";

interface TimelineHeaderProps {
    dataDateRange: {
        start: Date;
        end: Date;
    };
}

export const TimelineHeader = (props: TimelineHeaderProps) => {
    // Plot hooks
    const containerRef = useRef(null);
    const svgRef = useRef(null);
    const dimensions = useResizeObserver(containerRef);

    // Context selection
    const [contextSelection, setContextSelection] =
        useAtom(selectionContextAtom);

    // Date ranges
    const dateRange = useMemo(() => {
        if (contextSelection.dateRange) {
            return [
                contextSelection.dateRange.start,
                contextSelection.dateRange.end,
            ];
        }
        return [props.dataDateRange.start, props.dataDateRange.end];
    }, [contextSelection, props.dataDateRange]);

    // Create the D3 bar chart with brush
    useEffect(() => {
        if (dimensions.width === 0 || dimensions.height === 0) return;
        if (!dateRange) return;

        // Clear previous SVG content
        d3.select(svgRef.current).selectAll("*").remove();

        // Set up dimensions
        const margin = TIMELINE_MARGINS;
        const width = dimensions.width - margin.left - margin.right;
        const height = dimensions.height - margin.top - margin.bottom;

        // Create SVG
        const svg = d3
            .select(svgRef.current)
            .attr("viewBox", `0 0 ${dimensions.width} ${dimensions.height}`)
            .attr("width", dimensions.width)
            .attr("height", dimensions.height);

        const x = d3.scaleUtc().domain(dateRange).range([0, width]);

        // Create the chart group
        const chart = svg
            .append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        // BG rectangle to grab clicks
        chart
            .append("rect")
            .attr("x", 0)
            .attr("y", 0)
            .attr("width", width)
            .attr("height", height)
            .attr("fill", "transparent");

        // Compute ticks
        const { tickFormat, tickValues } = computePlotTicks({
            startDate: dateRange[0],
            endDate: dateRange[1],
            plotWidth: width,
        });

        // X Axis with guaranteed start/end ticks
        chart
            .append("g")
            .attr("class", "x-axis-full")
            .attr("transform", `translate(0,${height})`)
            .call(
                d3
                    .axisTop(x)
                    .tickValues(tickValues)
                    .tickSize(5)
                    .tickSizeOuter(0)
                    .tickPadding(2)
                    .tickFormat(tickFormat),
            );

        chart.select(".x-axis-full .domain").remove();
        chart
            .selectAll(".x-axis-full text")
            .attr("font-size", "12px")
            .attr("fill", "#777");

        // Create brush
        const brush = d3
            .brushX()
            .extent([
                [0, 0],
                [width, height],
            ])
            .on("end", (event) => {
                if (event.selection) {
                    const [x0, x1] = event.selection;
                    setContextSelection((s) => ({
                        ...s,
                        dateRange: {
                            start: x.invert(x0),
                            end: x.invert(x1),
                        },
                    }));
                }
            });

        // Append brush
        chart.append("g").attr("class", "brush").call(brush);

        // Create highlight marker group
        const highlightMarker = chart
            .append("g")
            .attr("class", "highlight-marker");

        // Vertical line (no pointer events)
        highlightMarker
            .append("line")
            .attr("class", "highlight-line")
            .attr("y1", height - 18)
            .attr("y2", height)
            .attr("stroke", "#3b82f6")
            .attr("stroke-width", 1.5)
            .style("pointer-events", "none");

        // Triangle pointing down at bottom (no pointer events)
        highlightMarker
            .append("path")
            .attr(
                "d",
                `M -6,${height - 18} L 0,${height - 12} L 6,${height - 18} Z`,
            ) // Triangle points down
            .attr("fill", "#3b82f6")
            .style("pointer-events", "none");

        // Label group (draggable)
        const labelGroup = highlightMarker
            .append("g")
            .attr("class", "label-group");

        // Label background (rounded square)
        labelGroup
            .append("rect")
            .attr("class", "highlight-label-bg")
            .attr("x", -50)
            .attr("y", height - 32)
            .attr("height", 14)
            .attr("width", 100)
            .attr("rx", 4)
            .attr("ry", 4)
            .attr("fill", "#3b82f6");

        // Label text
        labelGroup
            .append("text")
            .attr("class", "highlight-label")
            .attr("x", 0)
            .attr("y", height - 24)
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "middle")
            .text(tickFormat(dateRange[0]))
            .attr("fill", "white")
            .attr("font-size", "12px");

        // Update highlight marker based on highlightPosition
        if (contextSelection.highlightPosition) {
            const xPos = x(contextSelection.highlightPosition.date);
            highlightMarker
                .style("display", null)
                .attr("transform", `translate(${xPos}, 0)`);
            highlightMarker
                .select(".highlight-label")
                .text(tickFormat(contextSelection.highlightPosition.date));
        } else {
            highlightMarker.style("display", "none");
        }
    }, [
        dimensions,
        dateRange,
        setContextSelection,
        contextSelection.highlightPosition,
    ]);

    return (
        <div className="w-full h-8 flex items-center">
            <div className="w-[200px] text-xs p-2 flex items-center justify-between">
                <span>
                    {formatDate(dateRange[0])}
                    {" - "}
                    {formatDate(dateRange[1])}
                </span>
                {contextSelection.dateRange && (
                    <button
                        type="button"
                        className="text-ae-blue-550"
                        onClick={() => {
                            setContextSelection((s) => ({
                                ...s,
                                dateRange: undefined,
                            }));
                        }}
                    >
                        Reset
                    </button>
                )}
            </div>
            <div
                ref={containerRef}
                className="flex-1 w-full h-full border-l border-ae-slate-200"
            >
                <svg ref={svgRef} className="w-full h-full" />
            </div>
        </div>
    );
};
